import {ref, watch, computed, onMounted} from "vue";
import {useStateSocket} from "../../../../composables/state/socket";
import {onUnmounted} from "#imports";
import {useStateFunctionBodyClass} from "../../../../composables/state/body_class";
import {useNuxtApp} from "#app";
import ChatRoom from "../../../../lib/models/SocketIo/ChatRoom";
import Client from "../../../../lib/models/Client";

export default function BaseChat(props: { room: ChatRoom, client: Client }) {

    const chatRoom = ref(null);
    const sending = ref(false)
    const loading = ref(false);
    const targetTyping = ref(false);
    const messages = ref([]);
    const typingTimeout = ref(null);
    const lastMessageId = ref(null);
    const messageInput = ref(null);
    const verifyMessageSentTimeout = ref<null | number>(null);
    const previousVisibilityValue = ref(false);
    const pager = ref(0);
    const previousMessageCount = ref(0);
    const verifyMessageSentCounter = ref(0);
    const message = ref("");
    const lastError = ref("");

    const {socket_reconnecting} = useStateSocket();
    const {removeBodyClass, updateBodyClass} = useStateFunctionBodyClass();
    const {$isDesktop, $i18n, $socketApi, $socketIo} = useNuxtApp();


    watch(lastError, (val) => {
        if (val && val.length > 0) {
            setTimeout(() => {
                lastError.value = "";
            }, 5000);
        }
    });

    const groupedMessages = computed(() => {
        let id_user_previous = -1;
        const tMessages: Array<Array<{}>> = [];
        let i = -1;
        messages.value.forEach(message => {
            if (message.id_user != id_user_previous) {
                id_user_previous = message.id_user;
                tMessages.push(new Array());
                i++;
            }
            tMessages[i].unshift(message);
        })
        return tMessages;
    });

    const socketIoReconnecting = computed(() => {
        return socket_reconnecting.value;
    });

    const isPhoneDevice = computed(() => {
        return $isDesktop();
    });

    onUnmounted(() => {
        if (chatRoom.value) {
            leavePreviousRoom(chatRoom.code);
        }
        if (verifyMessageSentTimeout.value !== null) {
            clearInterval(verifyMessageSentTimeout.value);
        }

        if (typingTimeout.value !== null) {
            clearTimeout(typingTimeout.value);
        }
        if (verifyMessageSentTimeout.value !== null) {
            clearTimeout(verifyMessageSentTimeout.value);
        }

        removeBodyClass('no-scroll');
        document.removeEventListener("visibilitychange", handleReconnecting);
    });

    onMounted(() => {
        // document.removeEventListener("visibilitychange", handleReconnecting, true);
        document.addEventListener("visibilitychange", handleReconnecting);

        loadMessages();
        if (sessionStorage.getItem('last-chat-message')) {
            message.value = sessionStorage.getItem('last-chat-message');
            setTimeout(() => {
                updateHeight();
            }, 100);
        }
        if (isPhoneDevice.value) {
            updateBodyClass('no-scroll');
        }
    });

    const submitMessage = (i = 0) => {
        if (!sending.value && chatRoom.value) {
            sending.value = true;
            if (typingTimeout.value !== null) {
                clearTimeout(typingTimeout.value);
            }
            $socketIo.socket.value.emit('messenger_typing_end', {_user: props.client.socket_token, room: chatRoom.value.code});

            lastMessageId.value = Math.round(Math.random() * 90000);
            $socketIo.socket.value.emit('messenger_message_submit', {
                id: lastMessageId.value,
                _user: props.client.socket_token,
                room: chatRoom.value.code,
                message: message.value
            });
            sessionStorage.setItem('last-chat-message', message.value);

            // Verify if page was sent, if not, probably disconnected and need to be sent again
            verifyMessageSentCounter.value = 0;
            if (verifyMessageSentTimeout.value !== null) clearInterval(verifyMessageSentTimeout.value);
            verifyMessageSentTimeout.value = setInterval(() => {
                if (lastMessageId.value === -1) {
                    clearInterval(verifyMessageSentTimeout.value);
                    verifyMessageSentTimeout.value = null;
                } else {
                    verifyMessageSentCounter.value++;
                    if (verifyMessageSentCounter.value > 5 && i < 5) {
                        lastError.value = $i18n.t('unable-to-sent-message-new-attempt');
                        clearInterval(verifyMessageSentTimeout.value);
                        reconnectSocketIO();
                        verifyMessageSentTimeout.value = null;
                        sending.value = false;
                        return;
                        //lastError.value = $i18n.t('unable-to-sent-message-reloading');
                    }
                }
            }, 400);

            setTimeout(() => {

                if (messageInput.value) {
                    messageInput.value.focus();
                }
            }, 400);
        }
    };

    const toggleTyping = () => {
        if (typingTimeout.value !== null) {
            clearTimeout(typingTimeout.value);
        }
        if (chatRoom.value) {
            $socketIo.socket.value.emit('messenger_typing_start', {_user: props.client.socket_token, room: chatRoom.value.code});

            typingTimeout.value = setTimeout(() => {
                $socketIo.socket.value.emit('messenger_typing_end', {_user: props.client.socket_token, room: chatRoom.value.code});
                typingTimeout.value = null;
            }, 1500);
        }
    }

    const initSockets = () => {

        $socketIo.socket.value.off('messenger_message_receive');
        $socketIo.socket.value.on('messenger_message_receive', (resMessage) => {
            messages.value.unshift(resMessage);
            if (resMessage.room === props.room.code) {
                $socketIo.socket.value.emit('mark_message_as_read', {_user: props.client.socket_token, mid: resMessage.mid, rid: props.room.code});
            }
            scrollToBottom();
        })

        $socketIo.socket.value.off('messenger_typing_start');
        $socketIo.socket.value.on('messenger_typing_start', (resMessage) => {
            targetTyping.value = true;
        })

        $socketIo.socket.value.off('messenger_typing_end');
        $socketIo.socket.value.on('messenger_typing_end', (resMessage) => {
            targetTyping.value = false;
        })


        $socketIo.socket.value.off('server_error');
        $socketIo.socket.value.on('server_error', (resMessage) => {
            if (sending.value) {
                sending.value = false;
            }
            lastError.value = resMessage.message;
        })

        $socketIo.socket.value.on('messenger_message_submit_received', (resMessage) => {
            if ((Number(resMessage.m)) === lastMessageId.value) {
                // Message sent successfully
                sending.value = false;
                message.value = "";
                lastMessageId.value = -1;
                lastError.value = null;
                sessionStorage.removeItem('last-chat-message');
                setTimeout(() => {
                    updateHeight();
                }, 100);
            }
        })

    };

    const leavePreviousRoom = (roomCode) => {
        $socketIo.socket.value.emit('leave_room', {_user: props.client.socket_token, room: roomCode});
    };

    const loadMessages = () => {

        if (loading.value === true) {
            return;
        }
        loading.value = true;
        const loadRoomMessages = () => {
            previousMessageCount.value = messages.value.length;
            $socketApi.getSocketUrl("/api/chat/" + props.room.code + "/messages", {query: {page: pager.value}}).then((res) => {
                if (res.status === 'success' && Array.isArray(res.data)) {
                    messages.value = pager.value === 0 ? res.data : [].concat(messages.value).concat(res.data);
                    loading.value = false;
                    pager.value++;
                    res.data.forEach(m => {
                        if (m.seen == false) {
                            $socketIo.socket.value.emit('mark_message_as_read', {_user: props.client.socket_token, mid: m.mid, rid: props.room.code});
                        }
                    })
                }
            })
        }

        if (chatRoom.value === null) {
            pager.value = 0;
            previousMessageCount.value = 0;
            $socketApi.getSocketUrl("/api/chat/room", {query: {code: props.room.code}}).then((res) => {

                chatRoom.value = res.data;
                loadRoomMessages();
                initSockets();

                $socketIo.socket.value.emit('join_room', {_user: props.client.socket_token, room: props.room.code});
                scrollToBottom();

            }).catch((e) => {
                alert((e.response && e.response.data && e.response.data.message) ? e.response.data.message : `Chat error: ${e.message}`);
            })
        } else {
            loadRoomMessages();
        }

    }

    const handleReconnecting = () => {

        if (document.visibilityState === 'visible') {
            if (previousVisibilityValue.value === false) {
                reconnectSocketIO();
            }
            previousVisibilityValue.value = true;
        } else {
            $socketIo.socket.value.disconnect();
            previousVisibilityValue.value = false;
        }
    }

    const reconnectSocketIO = () => {
        pager.value = 0;
        previousMessageCount.value = 0;
        $socketIo.reconnect();
        loadMessages();
        initSockets();
    };

    const scrollToBottom = () => {
        const objDiv = document.getElementsByClassName("chat-room-message-list").item(0);
        if (objDiv) {
            setTimeout(() => {
                objDiv.scrollTop = 0;
                // objDiv.scrollTop = objDiv.scrollHeight;
            }, 400)

        }
    }


    const updateHeight = () => {
        if (messageInput.value && Number(messageInput.value.scrollHeight) < 200) {
            messageInput.value.style.height = 0;
            messageInput.value.style.height = messageInput.value.scrollHeight + 'px'
        }
    };

    return {
        groupedMessages,
        message,
        submitMessage,
        sending,
        socketIoReconnecting,
        messages,
        lastError,
        targetTyping,
        loading,
        chatRoom,
        previousMessageCount,
        toggleTyping,
        loadMessages,
        messageInput,
        updateHeight
    }
}
